import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Sparkles, Send, Loader2, TrendingUp, Settings2, History, Zap, KeyRound, DollarSign, Store, Save, ShieldCheck } from "lucide-react";
import { OutputPanel, FindingDetail, money } from "../components/swarm/OutputPanel";
import { StorePanel } from "../components/swarm/SwarmChrome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const getToken = () => new URLSearchParams(window.location.search).get("token") || "";

const TABS = [
  ["run", "Run", Zap], ["savings", "Savings", TrendingUp],
  ["history", "History", History], ["settings", "Settings", Settings2],
];

export default function EmbedApp() {
  const token = getToken();
  const client = axios.create({ baseURL: API, params: token ? { token } : {} });
  const [session, setSession] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("run");
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("retail");
  const [running, setRunning] = useState(false);
  const [runErr, setRunErr] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [routingMode, setRoutingMode] = useState("smart");
  const [exporting, setExporting] = useState(false);
  const [storeOpen, setStoreOpen] = useState(false);
  const [storeCards, setStoreCards] = useState([]);
  const [storeMeta, setStoreMeta] = useState({ regions: [], action_types: [] });
  const [storeFilter, setStoreFilter] = useState({ region: "", brand: "", priority: "", q: "" });

  useEffect(() => {
    client.get("/embed/session").then(({ data }) => setSession(data))
      .catch((e) => setErr(e.response?.data?.detail || "Invalid or missing embed token"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    if (!prompt.trim() || running) return;
    setRunning(true); setResult(null); setSelectedFinding(null); setRunErr(null);
    try {
      const { data } = await client.post("/embed/run", { prompt, mode });
      const jobId = data.job_id;
      const poll = async () => {
        const { data: j } = await client.get(`/embed/job/${jobId}`);
        if (j.status === "complete") { setResult(j.result); setRunning(false); }
        else if (j.status === "error") { setRunErr(j.error || "Run failed."); setRunning(false); }
        else setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    } catch (e) { setRunErr(e.response?.data?.detail || "Run failed. Please try again."); setRunning(false); }
  };

  const exportReport = async (fmt) => {
    if (!result) return; setExporting(true);
    try {
      let url;
      if (fmt === "pdf") {
        const { data } = await axios.post(`${API}/swarm/report.pdf`, { mode: result.mode, prompt: result.prompt, executive_summary: result.executive_summary || "", findings: result.findings, comparison: result.comparison, summary: result.summary, compute_method: result.compute_method }, { responseType: "blob" });
        url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      } else {
        const { data } = await axios.post(`${API}/swarm/export`, { format: fmt, mode: result.mode, prompt: result.prompt, executive_summary: result.executive_summary || "", scope_lines: result.scope_lines, findings: result.findings, comparison: result.comparison, summary: result.summary, compute_method: result.compute_method, cost_intelligence: result.cost_intelligence }, { responseType: "blob" });
        url = URL.createObjectURL(new Blob([data]));
      }
      const a = document.createElement("a");
      a.href = url; a.download = `QNNEAL_Report_${new Date().toISOString().slice(0, 10)}.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  if (err) return <Shell><div className="text-center py-20 text-slate-400" data-testid="embed-error"><ShieldCheck className="w-8 h-8 mx-auto mb-3 text-red-400" />{err}</div></Shell>;
  if (!session) return <Shell><div className="text-center py-20 text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div></Shell>;

  return (
    <Shell company={session.company}>
      <div className="flex items-center gap-1 border-b border-white/[0.08] px-4" data-testid="embed-tabs">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm border-b-2 transition-colors ${tab === k ? "border-[#14F0C8] text-[#14F0C8]" : "border-transparent text-slate-400 hover:text-slate-200"}`}
            data-testid={`embed-tab-${k}`}><Icon className="w-4 h-4" /> {label}</button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === "run" && (
          <div className="flex h-full">
            <div className="flex-1 p-6">
              <div className="max-w-2xl">
                <div className="text-lg font-bold mb-1" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Run the swarm</div>
                <div className="text-xs text-slate-400 mb-4">200 agents · quantum optimization · deterministic per prompt{session.custom_pricing ? " · your custom pricing active" : ""}</div>
                <div className="flex gap-2 mb-3">
                  {["retail", "jnj"].map((m) => (
                    <button key={m} onClick={() => setMode(m)} className={`text-xs px-3 py-1.5 rounded-full border ${mode === m ? "border-[#14F0C8] text-[#14F0C8]" : "border-white/10 text-slate-400"}`} data-testid={`embed-mode-${m}`}>{m === "retail" ? "Tailored Brands" : "Johnson & Johnson"}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2 rounded-2xl border border-white/[0.1] bg-[#0F1629]/90 pl-4 pr-2 py-2">
                  <input value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} disabled={running || !session.allow_run}
                    placeholder="Ask the swarm — e.g. Optimize size curves for our Southeast region"
                    className="flex-1 bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-500" data-testid="embed-prompt-input" />
                  <button onClick={run} disabled={running || !session.allow_run} className="w-9 h-9 rounded-xl bg-[#14F0C8] text-[#07110E] flex items-center justify-center disabled:opacity-50" data-testid="embed-run-btn">
                    {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
                {running && <div className="mt-4 text-sm text-[#14F0C8] flex items-center gap-2" data-testid="embed-running"><Loader2 className="w-4 h-4 animate-spin" /> 200 agents optimizing… (~20-40s on live models)</div>}
                {runErr && <div className="mt-4 text-sm text-red-400" data-testid="embed-run-error">{runErr}</div>}
                {!session.allow_run && <div className="mt-3 text-xs text-amber-400">This embed token is view-only (run disabled).</div>}
              </div>
            </div>
            {result && (
              <OutputPanel result={result} role="ceo" onClose={() => setResult(null)} onOpenStores={async () => { const { data } = await client.get("/embed/store-actions"); setStoreCards(data.cards); setStoreMeta({ regions: data.regions, action_types: data.action_types }); setStoreOpen(true); }}
                onExport={exportReport} exporting={exporting} onSelectFinding={setSelectedFinding} routingMode={routingMode} setRoutingMode={setRoutingMode} />
            )}
          </div>
        )}

        {tab === "savings" && <SavingsTab client={client} />}
        {tab === "history" && <HistoryTab client={client} onOpen={(r) => { setResult(r); setTab("run"); }} />}
        {tab === "settings" && <SettingsTab client={client} onSaved={() => client.get("/embed/session").then(({ data }) => setSession(data))} />}
      </div>

      {storeOpen && <StorePanel cards={storeCards} meta={storeMeta} filter={storeFilter} setFilter={setStoreFilter}
        onReload={async (f) => { const p = new URLSearchParams(f); const { data } = await client.get(`/embed/store-actions?${p}`); setStoreCards(data.cards); }} onClose={() => setStoreOpen(false)} />}
      {selectedFinding && <FindingDetail finding={selectedFinding} onClose={() => setSelectedFinding(null)} />}
    </Shell>
  );
}

const Shell = ({ children, company }) => (
  <div className="fixed inset-0 flex flex-col bg-[#05070E] text-slate-100 overflow-hidden" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }} data-testid="embed-app">
    <div className="flex-shrink-0 flex items-center gap-2 px-5 py-3 border-b border-white/[0.06] bg-[#070B14]/85">
      <Sparkles className="w-4 h-4 text-[#14F0C8]" />
      <span className="text-sm font-bold tracking-wide text-[#14F0C8]">QNNEAL</span>
      {company && <span className="text-xs text-slate-400">· {company}</span>}
      <span className="ml-auto text-[10px] text-slate-600" style={{ fontFamily: MONO }}>embedded</span>
    </div>
    {children}
  </div>
);

function SavingsTab({ client }) {
  const [d, setD] = useState(null);
  useEffect(() => { client.get("/embed/savings").then(({ data }) => setD(data)).catch(() => {}); }, []); // eslint-disable-line
  if (!d) return <Loading />;
  const Card = ({ label, value, accent }) => (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent || "text-slate-100"}`} style={{ fontFamily: MONO }}>{value}</div>
    </div>
  );
  return (
    <div className="p-6" data-testid="embed-savings">
      <div className="text-lg font-bold mb-4" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Cumulative Savings</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card label="Business value found" value={money(d.value_found)} accent="text-[#14F0C8]" />
        <Card label="Compute cost" value={`$${d.compute_cost.toFixed(2)}`} />
        <Card label="Compute saved vs naive" value={`$${d.compute_saved.toFixed(2)}`} accent="text-emerald-400" />
        <Card label="ROI on compute" value={`${d.roi_pct.toLocaleString()}%`} accent="text-emerald-400" />
        <Card label="Queries" value={d.queries} />
        <Card label="Agents deployed" value={d.agents_deployed.toLocaleString()} />
        <Card label="Compute savings" value={`${d.compute_savings_pct}%`} accent="text-emerald-400" />
      </div>
      <div className="rounded-xl border border-white/[0.07] overflow-hidden" style={{ fontFamily: MONO }}>
        <div className="grid grid-cols-4 gap-2 px-3 py-2 text-[10px] text-slate-500 bg-white/[0.03]"><span>Prompt</span><span className="text-right">Uplift</span><span className="text-right">Cost</span><span className="text-right">Saved</span></div>
        {d.recent.map((r) => (
          <div key={r.run_id} className="grid grid-cols-4 gap-2 px-3 py-2 text-[11px] text-slate-300 border-t border-white/[0.04]">
            <span className="truncate">{r.prompt}</span><span className="text-right text-[#14F0C8]">{money(r.uplift_usd)}</span><span className="text-right">${r.compute_cost.toFixed(2)}</span><span className="text-right text-emerald-400">${r.savings_abs.toFixed(2)}</span>
          </div>
        ))}
        {d.recent.length === 0 && <div className="px-3 py-6 text-center text-xs text-slate-600">No runs yet.</div>}
      </div>
    </div>
  );
}

function HistoryTab({ client, onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { client.get("/embed/history").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []); // eslint-disable-line
  if (!rows) return <Loading />;
  return (
    <div className="p-6" data-testid="embed-history">
      <div className="text-lg font-bold mb-4" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Query History</div>
      <div className="space-y-2">
        {rows.map((r) => (
          <button key={r.run_id} onClick={async () => { const { data } = await client.get(`/embed/run/${r.run_id}`); onOpen(data); }}
            className="w-full text-left rounded-xl border border-white/[0.07] bg-white/[0.02] hover:border-[#14F0C8]/40 p-3 transition-colors" data-testid={`embed-history-${r.run_id}`}>
            <div className="flex justify-between"><span className="text-sm text-slate-200">{r.prompt}</span><span className="text-sm font-bold text-[#14F0C8]" style={{ fontFamily: MONO }}>{money(r.uplift_usd)}</span></div>
            <div className="text-[10px] text-slate-500 mt-0.5" style={{ fontFamily: MONO }}>{r.created_at?.slice(0, 16).replace("T", " ")} · cost ${r.compute_cost?.toFixed(2)} · {r.mode}</div>
          </button>
        ))}
        {rows.length === 0 && <div className="text-center text-xs text-slate-600 py-8">No runs yet.</div>}
      </div>
    </div>
  );
}

function SettingsTab({ client, onSaved }) {
  const [s, setS] = useState(null);
  const [byok, setByok] = useState({});
  const [pricing, setPricing] = useState({});
  const [saved, setSaved] = useState(false);
  const load = useCallback(() => client.get("/embed/settings").then(({ data }) => {
    setS(data); setPricing({ ...data.pricing }); setByok({});
  }).catch(() => {}), []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  if (!s) return <Loading />;
  const save = async () => {
    const body = {};
    if (Object.keys(byok).length) body.byok = byok;
    if (Object.keys(pricing).length) body.pricing = pricing;
    await client.put("/embed/settings", body);
    setSaved(true); setTimeout(() => setSaved(false), 2000); load(); onSaved?.();
  };
  const LABELS = { dwave_api_key: "D-Wave Leap API Key", openai_api_key: "OpenAI API Key", anthropic_api_key: "Anthropic API Key", gemini_api_key: "Gemini API Key" };
  return (
    <div className="p-6 max-w-2xl" data-testid="embed-settings">
      <div className="flex items-center gap-2 mb-1"><KeyRound className="w-4 h-4 text-[#14F0C8]" /><span className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Bring Your Own Keys</span></div>
      <div className="text-xs text-slate-400 mb-4">Keys are stored encrypted per tenant. Leave blank to keep the current value.</div>
      <div className="space-y-3 mb-8">
        {Object.keys(LABELS).map((f) => (
          <div key={f}>
            <label className="text-[11px] text-slate-400 flex items-center justify-between">
              {LABELS[f]}
              {s.byok_configured[f] && <span className="text-emerald-400 text-[10px]">✓ configured ({s.byok[f]})</span>}
            </label>
            <input type="password" placeholder={s.byok_configured[f] ? "•••••• (set — leave blank to keep)" : "Paste key…"}
              value={byok[f] ?? ""} onChange={(e) => setByok({ ...byok, [f]: e.target.value })}
              className="w-full mt-1 bg-[#0F1629]/90 border border-white/[0.1] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#14F0C8]/50" data-testid={`byok-${f}`} />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-[#14F0C8]" /><span className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Model Pricing</span></div>
      <div className="text-xs text-slate-400 mb-4">Your negotiated per-token rates. Cost Intelligence uses these instead of QNNEAL defaults.</div>
      <div className="space-y-2 mb-8" style={{ fontFamily: MONO }}>
        <div className="grid grid-cols-3 gap-2 text-[10px] text-slate-500"><span>Model</span><span>QNNEAL default ($/token)</span><span>Your rate ($/token)</span></div>
        {Object.keys(s.default_pricing).map((m) => (
          <div key={m} className="grid grid-cols-3 gap-2 items-center">
            <span className="text-xs text-slate-300">{s.model_labels[m]}</span>
            <span className="text-[11px] text-slate-500">{s.default_pricing[m]}</span>
            <input type="number" step="0.0000001" value={pricing[m] ?? ""} placeholder={String(s.default_pricing[m])}
              onChange={(e) => setPricing({ ...pricing, [m]: parseFloat(e.target.value) || 0 })}
              className="bg-[#0F1629]/90 border border-white/[0.1] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#14F0C8]/50" data-testid={`price-${m}`} />
          </div>
        ))}
      </div>

      <button onClick={save} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#14F0C8] text-[#07110E] font-semibold text-sm hover:scale-[1.02] transition-transform" data-testid="embed-settings-save">
        <Save className="w-4 h-4" /> {saved ? "Saved ✓" : "Save settings"}
      </button>
    </div>
  );
}

const Loading = () => <div className="py-20 text-center text-slate-500"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>;
